import clsx from 'clsx';
import React from 'react';
import Button from '../../atoms/Button/Button';
import Icon from '../../atoms/Icon/Icon';
import useUploadArea from './useUploadArea';

/**
 * UploadArea - Zona presentacional de subida de ficheros (drag & drop + selector nativo).
 *
 * Es un componente "tonto": solo gestiona el estado visual de drag-over, el input
 * oculto y el click para abrir el selector. Emite los ficheros crudos via
 * `onFilesSelected` sin validar ni mantener la lista seleccionada.
 */

export type UploadAreaProps = Readonly<{
  acceptedExtensions: string[];
  buttonRef?: React.Ref<HTMLButtonElement>;
  buttonText?: string;
  className?: string;
  isDisabled?: boolean;
  isMultiple?: boolean;
  /** No more files fit: the picker is disabled, while a drop still reaches `onFilesSelected` so the form can explain the limit. */
  isAtCapacity?: boolean;
  maxSizeBytes: number;
  onFilesSelected: (files: File[]) => void;
  /** `button` renders only the picker button; the consumer owns the surrounding surface (background, border, radius). */
  variant?: 'default' | 'button';
}>;

function UploadArea({
  acceptedExtensions,
  buttonRef,
  buttonText,
  className,
  isDisabled = false,
  isMultiple = true,
  isAtCapacity = false,
  maxSizeBytes,
  onFilesSelected,
  variant = 'default',
}: UploadAreaProps): React.JSX.Element {
  const {
    acceptAttribute,
    allowedFormatsLabel,
    allowedFormatsText,
    browseText,
    dragAnnouncementText,
    dropTitleText,
    fileInputRef,
    handleClickSelect,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileInputChange,
    isDragOver,
    isFileAtCapacity,
    maxFileSizeLabel,
    maxFileSizeText,
  } = useUploadArea({ acceptedExtensions, buttonText, isDisabled, isAtCapacity, maxSizeBytes, onFilesSelected });

  return (
    <div className={clsx('ui-upload-area', `ui-upload-area--${variant}`, className)}>
      {/* Input oculto del arbol de accesibilidad: es solo el mecanismo que dispara el selector
          nativo. El control accesible es el boton "Browse" de mas abajo. */}
      <input
        ref={fileInputRef}
        accept={acceptAttribute}
        aria-hidden="true"
        className={clsx('ui-upload-area__input', 'sr-only')}
        disabled={isFileAtCapacity}
        multiple={isMultiple}
        tabIndex={-1}
        type="file"
        onChange={handleFileInputChange}
      />
      <div
        className={clsx('ui-upload-area__drag-container', {
          'ui-upload-area__drag-container--drag-over': isDragOver,
        })}
        role="presentation"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <span aria-live="polite" className="sr-only">
          {isDragOver ? dragAnnouncementText : ''}
        </span>
        {variant === 'button' ? (
          <Button
            ref={buttonRef}
            className="ui-upload-area__button"
            iconSize="lg"
            isDisabled={isFileAtCapacity}
            leftIcon="upload-image"
            onClick={handleClickSelect}
            size="sm"
            text={browseText}
            variant="primary-ghost"
          />
        ) : (
          <div className="ui-upload-area__drag-info">
            <span aria-hidden="true" className="ui-upload-area__drag-icon">
              <Icon color="primary" name="upload" size="xl" />
            </span>

            <div className="ui-upload-area__text-container">
              <span className="ui-upload-area__drag-title">{dropTitleText}</span>
              <button ref={buttonRef} className="ui-upload-area__link" disabled={isFileAtCapacity} type="button" onClick={handleClickSelect}>
                {browseText}
              </button>
            </div>

            <div className="ui-upload-area__max-size">
              {maxFileSizeText}: {maxFileSizeLabel}
            </div>
            <div className="ui-upload-area__max-size">
              {allowedFormatsText}: {allowedFormatsLabel}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UploadArea;
